import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { css, keyframes } from '@emotion/css';

import { useTravesia } from '#components/mobile/travesia/TravesiaContext';
import { Boton } from '#components/mobile/ui/Boton';
import { Hoja } from '#components/mobile/ui/Hoja';
import { color } from '#components/mobile/ui/tokens';
import { faltaPermisoUso, permisoUso, setUsoActivado } from '#uso';

/**
 * Ventana única del permiso de los datos de uso (RGPD y art. 22.2 LSSI).
 * Sale a quien no ha contestado («¿Me ayudas a mejorar Rumbo?») y, una vez,
 * a quien dijo «sí» a un texto anterior («He mejorado mis informes»). Nunca
 * durante la primera travesía. «De acuerdo» y «Prefiero que no» tienen el
 * mismo tamaño; tocar fuera la cierra hasta la próxima vez que se abra la app.
 */
export function PermisoUso({
  onCerrado,
  esperar = false,
}: {
  onCerrado?: () => void;
  /** Otra ventana de una vez (el aviso de datos inventados) va primero. */
  esperar?: boolean;
}) {
  const travesiaActiva = !!useTravesia()?.activa;
  const [visible, setVisible] = useState(faltaPermisoUso);
  const [renovar] = useState(() => permisoUso() === 'renovar');

  if (!visible || travesiaActiva || esperar) {
    return null;
  }

  const contestar = (si: boolean) => {
    setUsoActivado(si);
    cerrar();
  };

  function cerrar() {
    setVisible(false);
    onCerrado?.();
  }

  return (
    <HojaPermisoUso
      renovar={renovar}
      onSi={() => contestar(true)}
      onNo={() => contestar(false)}
      onCerrar={cerrar}
    />
  );
}

type HojaPermisoUsoProps = {
  /** Ya dijo «sí» a un texto anterior: «He mejorado mis informes». */
  renovar?: boolean;
  onSi: () => void;
  onNo: () => void;
  onCerrar: () => void;
};

/** La hoja en sí (sin estado), para poder probarla y hacerle capturas. */
export function HojaPermisoUso({
  renovar = false,
  onSi,
  onNo,
  onCerrar,
}: HojaPermisoUsoProps) {
  const { t } = useTranslation();
  return (
    <Hoja
      aria-label={t('Help improve Rumbo')}
      data-testid="permiso-uso"
      onCerrar={onCerrar}
    >
      <View style={{ gap: 12, paddingTop: 6 }}>
        <Text
          role="heading"
          aria-level={2}
          style={{
            fontSize: 21,
            fontWeight: 800,
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
            color: color.fg,
          }}
        >
          {renovar ? (
            <Trans>I have improved my reports</Trans>
          ) : (
            <Trans>Will you help me improve Rumbo?</Trans>
          )}
        </Text>
        <Text style={{ fontSize: 15, lineHeight: 1.45, color: color.fg2 }}>
          {renovar ? (
            <Trans>
              Now I keep better track of how you use Rumbo and what fails, so I
              can help you before you have to tell me.
            </Trans>
          ) : (
            <Trans>
              If you let me, I keep track of how you use Rumbo and what fails,
              so I can help you before you have to tell me.
            </Trans>
          )}
        </Text>
        <View
          style={{
            borderRadius: 12,
            padding: '10px 12px',
            gap: 2,
            backgroundColor: color.badSoft,
          }}
        >
          <Text
            style={{
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: color.bad,
            }}
          >
            <Trans>I have no access to</Trans>
          </Text>
          {[
            t('Amounts and balances'),
            t('Names of your accounts'),
            t('Categories and payees'),
            t('Nothing you type'),
          ].map(fila => (
            <Text
              key={fila}
              style={{ fontSize: 14, lineHeight: 1.4, color: color.fg }}
            >
              {fila}
            </Text>
          ))}
        </View>
        <Text style={{ fontSize: 15, lineHeight: 1.45, color: color.fg2 }}>
          <Trans>
            Your money stays locked with your key. Relax, this isn&apos;t
            gossip. I do the gossiping for free from the harbour railing.
          </Trans>
        </Text>
        <View style={{ gap: 10, marginTop: 4 }}>
          <Boton
            bloque
            className={botonVivo}
            data-testid="permiso-uso-si"
            onPress={onSi}
          >
            <Trans>Agreed</Trans>
          </Boton>
          <Boton
            bloque
            variante="fantasma"
            data-testid="permiso-uso-no"
            onPress={onNo}
            style={{
              backgroundColor: color.surface,
              boxShadow: `inset 0 0 0 1px ${color.line}`,
            }}
          >
            <Trans>I&apos;d rather not</Trans>
          </Boton>
        </View>
        <Text style={{ fontSize: 13, color: color.fg3, textAlign: 'center' }}>
          <Trans>You can change it at any time in Settings → Usage data.</Trans>
        </Text>
      </View>
    </Hoja>
  );
}

/** Latido suave y un brillo que cruza el botón: que den ganas de pulsarlo. */
const latido = keyframes({
  '0%, 70%, 100%': { transform: 'scale(1)' },
  '80%': { transform: 'scale(1.035)' },
  '90%': { transform: 'scale(0.995)' },
});

const brillo = keyframes({
  '0%, 55%': { transform: 'translateX(-120%) skewX(-20deg)' },
  '85%, 100%': { transform: 'translateX(320%) skewX(-20deg)' },
});

const botonVivo = css({
  position: 'relative',
  overflow: 'hidden',
  animation: `${latido} 2.8s ease-in-out infinite`,
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    width: '35%',
    background:
      'linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)',
    animation: `${brillo} 2.8s ease-in-out infinite`,
    pointerEvents: 'none',
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    '&::after': { display: 'none' },
  },
});
